import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from 'react-router-dom';
import { onAttributeDetail, onAttributeBasicUpdate, onDeleteAttributeChoice } from "../../controller/AttributeController";
import 'react-toastify/dist/ReactToastify.css';
import toast, { Toaster } from 'react-hot-toast';
import AuthUtility from "../../util/AuthUtility";
import { Col, Button, Row, Container, Card, Form, Table, Modal } from 'react-bootstrap';
import { AttributeEntity } from "../../dto/attribute/AttributeEntity";
import { AttributeChoiceEntity } from "../../dto/attribute/AttributeChoiceEntity";
import { AttributeDetailEntity } from "../../dto/attribute/AttributeDetailEntity";
import Delete from '@mui/icons-material/Delete';
import Add from '@mui/icons-material/Add';
import { onAttributeChoiceUpdate } from "../../controller/AttributeChoiceController";
import { Edit } from "@mui/icons-material";

type AttributeParams = {
    attributeId: string;
};

const AttributeDetail = () => {
    const { attributeId } = useParams<AttributeParams>();

    const navigate = useNavigate();

    const [attributeDetail, setAttributeDetail] = useState<AttributeDetailEntity>({});
    const [attribute, setAttribute] = useState<AttributeEntity>({});

    useEffect(() => {
        if (!AuthUtility.isUserLoggedIn()) {
            navigate("/login");
            window.location.reload();
        } else {
            attributeDetailGet();
        }
    }, []);

    // attribute choice update
    const attributeDetailGet = async () => {
        const response = await onAttributeDetail(parseInt(attributeId!));
        const data = response.data;
        if (data !== undefined) {


            setAttributeDetail({
                attribute: data.attribute
                    ? {
                        ...data.attribute,
                        createdAt: data.attribute.createdAt
                            ? new Date(data.attribute.createdAt)
                            : undefined,
                        updatedAt: data.attribute.updatedAt
                            ? new Date(data.attribute.updatedAt)
                            : undefined,
                    }
                    : undefined,
                choices: data.choices
            });

            setBasicInfo({
                code: data.attribute?.code || "",
                name: data.attribute?.name || "",
                dataType: data.attribute?.dataType || "",
                uiType: data.attribute?.uiType || "",
                status: data.attribute?.status || "",
                verificationRequired: !!data.attribute?.verificationRequired,
            })
        }
    }

    const [basicInfo, setBasicInfo] = useState<AttributeEntity>({
        code: "",
        name: "",
        dataType: "",
        uiType: "",
        verificationRequired: false,
        status: ""
    });

    const handleSaveBasicInfo = async () => {
        try {
            const payload = {
                id: parseInt(attributeId!),
                ...basicInfo,
            };

            // TODO: replace with your real API
            const res = await onAttributeBasicUpdate(payload);

            if (res.status === 0) {
                toast.success(res.msg);
                attributeDetailGet(); // reload from backend
            } else {
                toast.error(res.msg);
            }

        } catch (e) {
            console.error(e);
            toast.error("Failed to update basic information");
        }
    };

    const [showChoiceModal, setShowChoiceModal] = useState(false);
    const [editingChoice, setEditingChoice] = useState(false); // if editing choice

    const [newChoice, setNewChoice] = useState<AttributeChoiceEntity | undefined>({
        valueText: "",
        status: "ACTIVE"
    });

    const deleteAttributeChoice = async (choiceId?: number) => {
        const confirmed = window.confirm("Are you sure you want to delete this attribute choice?");
        if (!confirmed) return;

        try {
            // TODO: replace with your real API
            const res = await onDeleteAttributeChoice(choiceId);
            if (res.status == 0) {
                toast.success(res.msg);
            } else {
                toast.error(res.msg);
            }

            attributeDetailGet();
        } catch (e) {
            console.error(e);
            toast.error("Failed to delete attribute choice");
        }
    }

    // ================= ADD MEDIA HANDLER =================
    const handleSaveChoice = async () => {
        const payload = {
            ...newChoice,
            attributeId: parseInt(attributeId!), // ensure categoryId is present
        };

        try {

            // TODO: replace with your real API
            const res = await onAttributeChoiceUpdate(payload);
            if (res.status == 0) {
                toast.success(res.msg);
            } else {
                toast.error(res.msg);
            }

            // reset + close
            setShowChoiceModal(false);
            setEditingChoice(false);
            setNewChoice({ valueText: "", status: "ACTIVE" });

            // reload list
            attributeDetailGet();

        } catch (e) {
            console.error(e);
            toast.error("Media upload failed");
        }
    };

    return (
        <Container fluid className="p-3">
            <div><Toaster position="top-right" reverseOrder={false} /></div>

            {/* Header */}
            <Row className="align-items-center mb-3">
                <Col>
                    <h4 className="mb-0">
                        <a
                            style={{ cursor: "pointer", marginRight: 10 }}
                            onClick={() => navigate(-1)}
                        >
                            ←
                        </a>
                        Attribute Detail
                    </h4>
                </Col>
            </Row>

            <Card className="mb-3">
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <span>Basic Information</span>
                    <Button size="sm" onClick={handleSaveBasicInfo}>
                        Save
                    </Button>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-2">
                                <Form.Label>Code</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={basicInfo.code}
                                    onChange={(e) =>
                                        setBasicInfo(prev => ({ ...prev, code: e.target.value }))
                                    }
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-2">
                                <Form.Label>Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={basicInfo.name}
                                    onChange={(e) =>
                                        setBasicInfo(prev => ({ ...prev, name: e.target.value }))
                                    }
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-2">
                                <Form.Label>Data type</Form.Label>
                                <Form.Select
                                    value={basicInfo.dataType}
                                    onChange={(e) =>
                                        setBasicInfo(prev => ({ ...prev, dataType: e.target.value }))
                                    }
                                >
                                    <option value="TEXT">TEXT</option>
                                    <option value="NUMBER">NUMBER</option>
                                    <option value="BOOLEAN">BOOLEAN</option>
                                    <option value="CHOICE">CHOICE</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-2">
                                <Form.Label>UI type</Form.Label>
                                <Form.Select
                                    value={basicInfo.uiType}
                                    onChange={(e) =>
                                        setBasicInfo(prev => ({ ...prev, uiType: e.target.value }))
                                    }
                                >
                                    <option value="TEXTBOX">TEXTBOX</option>
                                    <option value="DROPDOWN">DROPDOWN</option>
                                    <option value="COLOR">COLOR</option>
                                    <option value="IMAGE">IMAGE</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-2">
                                <Form.Label>Status</Form.Label>
                                <Form.Select
                                    value={basicInfo.status}
                                    onChange={(e) =>
                                        setBasicInfo(prev => ({ ...prev, status: e.target.value }))
                                    }
                                >
                                    <option value="ACTIVE">ACTIVE</option>
                                    <option value="DRAFT">DRAFT</option>
                                    <option value="DISABLED">DISABLED</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Check
                                label="Verification required"
                                checked={!!basicInfo.verificationRequired}
                                onChange={(e) =>
                                    setBasicInfo(prev => ({ ...prev, verificationRequired: e.target.checked }))
                                }
                            />
                            <div>Created At: {attributeDetail.attribute?.createdAt?.toLocaleDateString() || '-'}</div>
                            <div>Updated At: {attributeDetail.attribute?.updatedAt?.toLocaleDateString() || '-'}</div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* ============ ATTRIBUTES ============ */}
            <Card className="mb-3">
                <Card.Header className="d-flex justify-content-between">
                    <span>Attributes Choices({attributeDetail?.choices?.length || 0})</span>
                    <Button
                        size="sm"
                        onClick={async () => {
                            setShowChoiceModal(true);

                            // reset
                            setNewChoice(undefined);
                        }}
                    >
                        + Add Choice
                    </Button>
                </Card.Header>
                <Card.Body>
                    <Table bordered hover>
                        <thead>
                            <tr>
                                <th>Value</th>
                                <th>Status</th>
                                <th>Sort order</th>
                                <th>Default Weight grams</th>
                                <th>Default length mm</th>
                                <th>Default width mm</th>
                                <th>Default height mm</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {attributeDetail?.choices?.map(a => (
                                <React.Fragment key={a.id}>
                                    <tr>
                                        <td>{a.valueText}</td>
                                        <td>{a.status}</td>
                                        <td>{a.sortOrder}</td>
                                        <td>{a.defaultWeightGrams}</td>
                                        <td>{a.defaultLengthMm}</td>
                                        <td>{a.defaultWidthMm}</td>
                                        <td>{a.defaultHeightMm}</td>
                                        <td>
                                            {/* <a onClick={() => deleteAttributeChoice(a.id)}>
                                                <Edit />
                                            </a>
                                            <a onClick={() => deleteAttributeChoice(a.id)}>
                                                <Delete />
                                            </a> */}

                                            <Button
                                            size="sm"
                                            variant="outline-primary"
                                            onClick={() => {
                                                setEditingChoice(true);

                                                setNewChoice({
                                                    id: a.id,
                                                    status: a.status,
                                                    valueText: a.valueText,
                                                    sortOrder: a.sortOrder,
                                                    defaultWeightGrams: a.defaultWeightGrams,
                                                    defaultLengthMm: a.defaultLengthMm,
                                                    defaultWidthMm: a.defaultWidthMm,
                                                    defaultHeightMm: a.defaultHeightMm
                                                });

                                                setShowChoiceModal(true);
                                            }}
                                            >
                                            Edit
                                            </Button>
                                            <Button
                                            size="sm"
                                            variant="outline-danger"
                                            onClick={() => deleteAttributeChoice(a.id)}
                                            >
                                            Delete
                                            </Button>
                                        </td>
                                    </tr>
                                </React.Fragment>
                            ))}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            {/* ============ CREATE ATTRIBUTE CHOICE MODAL ============ */}
            <Modal show={showChoiceModal}
                onHide={() => {
                    setShowChoiceModal(false);
                    setNewChoice({ status: "ACTIVE" });
                }} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {editingChoice ? "Edit Attribute Choice" : "Add Attribute Choice"}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <Form>
                        {/* STATUS */}
                        <Form.Group className="mb-3">
                            <Form.Label>Value</Form.Label>
                            <Form.Control
                                value={newChoice?.valueText || ""}
                                onChange={(e) =>
                                    setNewChoice({ ...newChoice, valueText: e.target.value })
                                }
                            />
                        </Form.Group>

                        {/* STATUS */}
                        <Form.Group className="mb-3">
                            <Form.Label>Status</Form.Label>
                            <Form.Select
                                value={newChoice?.status || "ACTIVE"}
                                onChange={(e) =>
                                    setNewChoice({ ...newChoice, status: e.target.value })
                                }
                            >
                                <option value="ACTIVE">ACTIVE</option>
                                <option value="DRAFT">DRAFT</option>
                                <option value="DISABLED">DISABLED</option>
                                <option value="DELETED">DELETED</option>
                            </Form.Select>
                        </Form.Group>

                        {/* SORT ORDER */}
                        <Form.Group>
                            <Form.Label>Sort Order</Form.Label>
                            <Form.Control
                                type="number"
                                value={newChoice?.sortOrder}
                                onChange={(e) => {
                                    setNewChoice({
                                        ...newChoice,
                                        sortOrder: Number(e.target.value)
                                    });
                                }
                                }
                            />
                        </Form.Group>

                        {/* WEIGHT GRAMS */}
                        <Form.Group>
                            <Form.Label>Default Weight Grams</Form.Label>
                            <Form.Control
                                type="number"
                                value={newChoice?.defaultWeightGrams}
                                onChange={(e) => {
                                    setNewChoice({
                                        ...newChoice,
                                        defaultWeightGrams: Number(e.target.value)
                                    });
                                }
                                }
                            />
                        </Form.Group>

                        {/* LENGTH MM */}
                        <Form.Group>
                            <Form.Label>Default Length mm</Form.Label>
                            <Form.Control
                                type="number"
                                value={newChoice?.defaultLengthMm}
                                onChange={(e) => {
                                    setNewChoice({
                                        ...newChoice,
                                        defaultLengthMm: Number(e.target.value)
                                    });
                                }
                                }
                            />
                        </Form.Group>

                        {/* WIDTH MM */}
                        <Form.Group>
                            <Form.Label>Default Width mm</Form.Label>
                            <Form.Control
                                type="number"
                                value={newChoice?.defaultWidthMm}
                                onChange={(e) => {
                                    setNewChoice({
                                        ...newChoice,
                                        defaultWidthMm: Number(e.target.value)
                                    });
                                }
                                }
                            />
                        </Form.Group>

                        {/* HEIGHT MM */}
                        <Form.Group>
                            <Form.Label>Default Height mm</Form.Label>
                            <Form.Control
                                type="number"
                                value={newChoice?.defaultHeightMm}
                                onChange={(e) => {
                                    setNewChoice({
                                        ...newChoice,
                                        defaultHeightMm: Number(e.target.value)
                                    });
                                }
                                }
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>

                <Modal.Footer>
                    <Button
                        variant="primary"
                        onClick={handleSaveChoice}
                    >
                        Upload
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    )
}

export default AttributeDetail;